import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import CategorySelect from '../components/CategorySelect';
import RadiusFilter from '../components/RadiusFilter';
import MultiCategoryFilter from '../components/MultiCategoryFilter';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { mockOffers, categories } from '../mock';
import { MapPin, Search, Filter, Navigation, Heart } from 'lucide-react';
import { getAllCategories, getSubcategories } from '../data/categories';
import { useFavorites } from '../context/FavoritesContext';
import { toast } from 'sonner';
import api from '../api/axios';

// Composant pour mettre à jour la vue de la carte
const MapViewController = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && zoom) {
      map.setView(center, zoom, {
        animate: true,
        duration: 1
      });
    }
  }, [center, zoom, map]);
  
  return null;
};

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Create colored markers using SVG for better visibility
const createCustomIcon = (color) => {
  const svgIcon = `
    <svg width="32" height="45" viewBox="0 0 32 45" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 13 16 29 16 29s16-16 16-29c0-8.837-7.163-16-16-16z" 
            fill="${color}" 
            stroke="white" 
            stroke-width="2"/>
      <circle cx="16" cy="16" r="6" fill="white"/>
    </svg>
  `;
  
  return L.divIcon({
    className: 'custom-leaflet-marker',
    html: svgIcon,
    iconSize: [32, 45],
    iconAnchor: [16, 45],
    popupAnchor: [0, -45]
  });
};

const MapPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]); // Multi-select
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [offers, setOffers] = useState([]); // Raw offers from server
  const [users, setUsers] = useState([]); // Users to display on map
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [profileAddress, setProfileAddress] = useState(null); // Adresse du profil
  const [selectedRadius, setSelectedRadius] = useState(null);
  const { toggleFavorite, isFavorite } = useFavorites();
  
  // État pour le contrôle dynamique de la carte
  const [mapCenter, setMapCenter] = useState([48.81, 2.34]); // Paris par défaut
  const [mapZoom, setMapZoom] = useState(11);
  
  // Coordonnées du centre de la France pour le mode "Tout"
  const franceCenterCoords = [46.603354, 1.888334];
  
  const [loading, setLoading] = useState(true);
  const allCategories = getAllCategories();

  // Fonction pour géocoder une adresse
  const geocodeAddress = async (address) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&countrycodes=fr&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const coords = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
        console.log(`✅ Géocodage réussi pour "${address}":`, coords);
        return coords;
      }
      return null;
    } catch (error) {
      console.error('Erreur de géocodage:', error);
      return null;
    }
  };

  // Charger l'adresse du profil au montage
  useEffect(() => {
    const loadUserProfile = async () => {
      // 1. Vérifier si position sauvegardée dans localStorage
      const savedLocation = localStorage.getItem('userLocation');
      if (savedLocation) {
        try {
          const coords = JSON.parse(savedLocation);
          setUserLocation(coords);
          console.log('✅ Position récupérée du cache:', coords);
          toast.success('Position mémorisée', { 
            description: 'Votre dernière position a été restaurée' 
          });
          return; // Ne pas continuer si on a une position en cache
        } catch (e) {
          console.error('Erreur lors de la lecture du cache:', e);
        }
      }

      // 2. Sinon, charger depuis le profil
      try {
        const response = await api.get('/auth/me');
        const userData = response.data;
        
        if (userData.address) {
          console.log('📍 Adresse du profil:', userData.address);
          
          // Géocoder l'adresse du profil
          const coords = await geocodeAddress(userData.address);
          if (coords) {
            setProfileAddress(coords);
            setUserLocation(coords);
            // Sauvegarder dans localStorage
            localStorage.setItem('userLocation', JSON.stringify(coords));
            console.log('💾 Position sauvegardée dans localStorage');
            toast.success('Position détectée', { 
              description: `Basée sur votre adresse: ${userData.address}` 
            });
          }
        }
      } catch (error) {
        console.log('⚠️ Impossible de charger le profil, utilisation de la géolocalisation navigateur');
        // 3. Fallback sur la géolocalisation navigateur
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const userPos = [position.coords.latitude, position.coords.longitude];
              setUserLocation(userPos);
              // Sauvegarder dans localStorage
              localStorage.setItem('userLocation', JSON.stringify(userPos));
              console.log('✅ Géolocalisation navigateur:', userPos);
              console.log('💾 Position sauvegardée dans localStorage');
            },
            (error) => {
              console.log('⚠️ Géolocalisation refusée');
            }
          );
        }
      }
    };

    loadUserProfile();
  }, []);

  const loadOffers = async () => {
    try {
      setLoading(true);
      
      // Build query params (only for server-side filters)
      let queryParams = 'status=approved';
      if (selectedCategories.length > 0) {
        // Pour l'instant, filtrer côté client si plusieurs catégories
        queryParams += `&category=${selectedCategories[0]}`;
      }
      if (selectedSubcategory) {
        queryParams += `&subcategory=${encodeURIComponent(selectedSubcategory)}`;
      }
      
      const response = await api.get(`/offers?${queryParams}`);
      setOffers(response.data);
      
      // Also load users for the map
      try {
        const usersResponse = await api.get('/users/map');
        setUsers(usersResponse.data || []);
        console.log(`👥 ${usersResponse.data?.length || 0} utilisateurs chargés pour la carte`);
      } catch (err) {
        console.warn('⚠️ Impossible de charger les utilisateurs:', err);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading offers:', error);
      setOffers(mockOffers.filter(o => o.status === 'approved'));
    } finally {
      setLoading(false);
    }
  };

  // Load offers only when categories/subcategory changes (server-side filters)
  useEffect(() => {
    loadOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories, selectedSubcategory]);
  
  // Fonction pour calculer la distance entre deux points (formule Haversine)
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance en km
  }, []);
  
  // Client-side filtering with useMemo (prevents unnecessary re-renders)
  const filteredOffers = useMemo(() => {
    let result = [...offers];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(offer =>
        offer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offer.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offer.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply radius filter
    if (selectedRadius && userLocation) {
      result = result.filter(offer => {
        const distance = calculateDistance(
          userLocation[0], 
          userLocation[1], 
          offer.latitude, 
          offer.longitude
        );
        return distance <= selectedRadius;
      });
    }
    
    return result;
  }, [offers, searchTerm, selectedRadius, userLocation, calculateDistance]);
  
  const handleCategoryChange = (selectedCats) => {
    setSelectedCategories(selectedCats);
    setSelectedSubcategory(''); // Reset subcategory when categories change
  };

  const getUserLocation = async () => {
    // Priorité 1 : Utiliser l'adresse du profil si disponible
    if (profileAddress) {
      setUserLocation(profileAddress);
      setMapCenter(profileAddress);
      setMapZoom(14);
      toast.success('Position détectée !', { 
        description: 'Basée sur l\'adresse de votre profil' 
      });
      return;
    }
    
    // Priorité 2 : Essayer de charger l'adresse du profil
    try {
      const response = await api.get('/auth/me');
      const userData = response.data;
      
      if (userData.address) {
        const coords = await geocodeAddress(userData.address);
        if (coords) {
          setProfileAddress(coords);
          setUserLocation(coords);
          setMapCenter(coords);
          setMapZoom(14);
          toast.success('Position détectée !', { 
            description: `Basée sur votre adresse: ${userData.address}` 
          });
          return;
        }
      }
    } catch (error) {
      console.log('Impossible de charger l\'adresse du profil');
    }
    
    // Priorité 3 : Fallback sur la géolocalisation navigateur
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = [position.coords.latitude, position.coords.longitude];
          setUserLocation(location);
          setMapCenter(location);
          setMapZoom(14);
          toast.success('Position détectée !', { 
            description: 'Via géolocalisation GPS du navigateur' 
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error('Erreur', { 
            description: 'Impossible de détecter votre position. Ajoutez une adresse dans votre profil.' 
          });
        }
      );
    }
  };

  // Handler pour le changement de rayon avec zoom automatique
  const handleRadiusChange = (radius) => {
    setSelectedRadius(radius);
    
    if (radius === null) {
      // Mode "Tout" - Zoom sur toute la France
      setMapCenter(franceCenterCoords);
      setMapZoom(6);
      toast.info('Vue France entière', { description: 'Affichage de toutes les offres' });
    } else if (userLocation) {
      // Zoom sur la position de l'utilisateur avec un niveau adapté au rayon
      setMapCenter(userLocation);
      
      // Définir le niveau de zoom en fonction du rayon
      let newZoom;
      switch(radius) {
        case 1:
          newZoom = 14; // Très proche pour 1km
          break;
        case 5:
          newZoom = 12; // Proche pour 5km
          break;
        case 10:
          newZoom = 11; // Moyen pour 10km
          break;
        case 20:
          newZoom = 10; // Large pour 20km
          break;
        default:
          newZoom = 11;
      }
      
      setMapZoom(newZoom);
      toast.success(`Rayon ${radius}km`, { description: `Zoom adapté à ${radius}km autour de vous` });
    } else {
      toast.success(`Rayon ${radius}km`, { description: 'Activez la géolocalisation pour centrer la carte' });
    }
  };

  const getMarkerColor = (offer) => {
    if (offer.category === 'allianceHumaine') return categories.allianceHumaine.color;
    if (offer.category === 'allianceEvasion') return categories.allianceEvasion.color;
    return '#6b7280';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-100 to-pink-200">
      <Navbar />
      <style>{`
        .custom-leaflet-marker {
          background: none !important;
          border: none !important;
        }
        .custom-leaflet-marker svg {
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
          cursor: pointer;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 12px !important;
        }
        .leaflet-popup-content {
          margin: 0 !important;
        }
        .leaflet-popup {
          z-index: 10000 !important;
        }
        /* Ensure dropdown appears above map */
        [role="listbox"] {
          z-index: 10001 !important;
        }
        [data-radix-popper-content-wrapper] {
          z-index: 10001 !important;
        }
      `}</style>
      <div className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-800 mb-4">
              Carte Interactive des Offres Solidaires
            </h1>
            <p className="text-gray-600 text-lg">
              Explorez {filteredOffers.length} offres solidaires autour de vous
            </p>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher une offre..."
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                {/* Filtre multi-catégories */}
                <MultiCategoryFilter
                  selectedCategories={selectedCategories}
                  onChange={handleCategoryChange}
                />
                
                <Button
                  onClick={getUserLocation}
                  className="bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600"
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Me Géolocaliser
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Radius Filter */}
          <div className="mb-6">
            <RadiusFilter
              userLocation={userLocation}
              onRadiusChange={handleRadiusChange}
              selectedRadius={selectedRadius}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Map - Agrandie (3 colonnes sur 4) */}
            <div className="lg:col-span-3">
              <Card className="h-[600px] overflow-hidden shadow-xl">
                <MapContainer
                  center={[48.81, 2.34]}
                  zoom={11}
                  style={{ height: '100%', width: '100%' }}
                  className="rounded-lg"
                  scrollWheelZoom={true}
                >
                  <MapViewController center={mapCenter} zoom={mapZoom} />
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  />
                  {filteredOffers.map((offer) => (
                    <Marker
                      key={offer.id}
                      position={[offer.latitude, offer.longitude]}
                      icon={createCustomIcon(getMarkerColor(offer))}
                      eventHandlers={{
                        click: () => {
                          console.log('🎯 Marker clicked:', offer.title);
                        }
                      }}
                    >
                      <Popup maxWidth={300} closeButton={true} autoPan={true}>
                        <div className="p-3 min-w-[250px]">
                          <h3 className="font-bold text-base mb-2">{offer.title}</h3>
                          <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                            {offer.description}
                          </p>
                          <Badge className="mb-3" style={{ backgroundColor: getMarkerColor(offer) }}>
                            {offer.subcategory}
                          </Badge>
                          <div className="flex gap-2">
                            <Link to={`/offre/${offer.id}`} className="flex-1">
                              <Button size="sm" className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600">
                                📍 Voir Détails
                              </Button>
                            </Link>
                            {offer.createdBy && (
                              <Link to={`/profil/${offer.createdBy}`}>
                                <Button size="sm" variant="outline" className="h-full">
                                  👤 Profil
                                </Button>
                              </Link>
                            )}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  
                  {/* User markers */}
                  {users.map((user, idx) => (
                    <Marker
                      key={user.id ? `user-${user.id}` : `user-fallback-${idx}`}
                      position={[user.latitude, user.longitude]}
                      icon={createCustomIcon('#3b82f6')}
                      eventHandlers={{
                        click: () => {
                          console.log('👤 User marker clicked:', user.name);
                        }
                      }}
                    >
                      <Popup maxWidth={300} closeButton={true} autoPan={true}>
                        <div className="p-3 min-w-[250px]">
                          <div className="flex items-center gap-3 mb-3">
                            <img 
                              src={user.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'} 
                              alt={user.name}
                              className="w-12 h-12 rounded-full"
                            />
                            <div>
                              <h3 className="font-bold text-base">{user.name}</h3>
                              <p className="text-xs text-gray-500">{user.address}</p>
                            </div>
                          </div>
                          {user.competences && user.competences.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs font-semibold text-gray-600 mb-1">Compétences:</p>
                              <div className="flex flex-wrap gap-1">
                                {user.competences.slice(0, 3).map((comp, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {typeof comp === 'string' ? comp : (comp.categoryName || comp.name || 'Compétence')}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          <Link to={`/profil/${user.id}`}>
                            <Button size="sm" className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
                              👤 Voir Profil
                            </Button>
                          </Link>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  
                  {userLocation && (
                    <Marker 
                      position={userLocation} 
                      icon={createCustomIcon('#ff6b6b')}
                    >
                      <Popup closeButton={true}>
                        <div className="p-2 text-center">
                          <p className="font-semibold text-base">📍 Vous êtes ici</p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
              </Card>
            </div>

            {/* Offers List - Réduite (1 colonne sur 4) */}
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2"
                 style={{scrollbarWidth: 'thin', scrollbarColor: '#9333ea #f3f4f6'}}>
              {filteredOffers.map((offer) => (
                <Card
                  key={offer.id}
                  className={`cursor-pointer hover:shadow-lg transition-all duration-300 ${
                    selectedOffer?.id === offer.id ? 'ring-2 ring-emerald-500' : ''
                  }`}
                  onClick={() => {
                    setSelectedOffer(offer);
                    // Ne change PLUS le centre ni le zoom pour garder les marqueurs visibles
                  }}
                >
                  {offer.image && (
                    <div className="h-28 overflow-hidden">
                      <img
                        src={offer.image}
                        alt={offer.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader className="pb-2 px-3 pt-3">
                    <div className="flex justify-between items-start gap-2">
                      <CardTitle className="text-sm leading-tight">{offer.title}</CardTitle>
                      <div className="flex items-center gap-1 shrink-0">
                        <Badge
                          className="text-xs px-1.5 py-0.5"
                          style={{
                            backgroundColor: getMarkerColor(offer),
                            color: 'white'
                          }}
                        >
                          {offer.subcategory}
                        </Badge>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(offer.id);
                          }}
                          className="hover:scale-110 transition-transform"
                        >
                          <Heart
                            className={`w-5 h-5 ${
                              isFavorite(offer.id)
                                ? 'fill-red-500 text-red-500'
                                : 'text-gray-400 hover:text-red-500'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="px-3 pb-3">
                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                      {offer.description}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                      <MapPin className="w-3 h-3" />
                      <span className="line-clamp-1">{offer.address}</span>
                      {offer.postal_code && (
                        <Badge variant="outline" className="ml-auto text-xs h-5 px-2 bg-purple-50 text-purple-700 border-purple-300">
                          {offer.postal_code}
                        </Badge>
                      )}
                      {!offer.postal_code && offer.department && (
                        <Badge variant="outline" className="ml-auto text-xs h-5 px-2 bg-purple-50 text-purple-700 border-purple-300">
                          Dept. {offer.department}
                        </Badge>
                      )}
                    </div>
                    <Link to={`/offre/${offer.id}`}>
                      <Button size="sm" className="w-full h-7 text-xs">
                        Voir Plus
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MapPage;